import {
  createStore,
  createEvent,
  createNode,
  launch,
  step,
  Step,
  sample
} from '@effector/effector'
import {MAX_IN_FLIGHT_ITEMS, TIMELINE_MAX} from '../env'
import {runtimeID, onRuntimeEvent} from './runtimeID'
import {runRuntime} from '../workingSet'

type Stack = {
  value: any
  parent: Stack | null
  node: Step
}
function printMeta(meta) {
  let result = ''
  if ('name' in meta) result += `${meta.name} `
  // if ('unit' in meta) {
  //   if (meta.unit in meta) result += `[${meta.unit}/${meta[meta.unit]}] `
  //   else result += `[${meta.unit}] `
  // }
  if ('op' in meta) {
    if (meta.op in meta) result += `[${meta.op}/${meta[meta.op]}] `
    else result += `[${meta.op}] `
  }
  return result.trim()
}
type TraceStep = {
  value: any
  node: Step
  stack: Stack
}
function unwind(
  stack: Stack,
  implicitStack: Stack[]
): Array<{
  value: any
  node: Step
  stack: Stack
}> {
  const result = []
  unwindCurrentStack(result, stack)
  for (let i = implicitStack.length - 1; i >= 0; i--) {
    unwindCurrentStack(result, implicitStack[i])
  }
  return result
}
function unwindCurrentStack(
  result: Array<{
    value: any
    node: Step
    stack: Stack
  }>,
  stack: Stack
) {
  while (stack) {
    result.push({
      value: stack.value,
      node: stack.node,
      stack
    })
    stack = stack.parent
  }
}
export type UpdateTrace = {
  runtimeID: string
  updateID: number
  stack: TraceStep[]
}
export type TraceTree = {
  runtimeID: string
  updateID: number
  node: Step
  nodeType: 'store' | 'event' | 'effect' | 'watch' | 'unit'
  name: string
  value: any
  stack: Stack
  parent: TraceTree | null
  child: TraceTree[]
  used: boolean
}
const traceTreeCleanup = createEvent()
const unitUpdate = createEvent<UpdateTrace>()
export const unitUpdates = createStore<UpdateTrace[]>([]).on(
  runtimeID,
  (list, runtimeID) => list.filter(record => record.runtimeID === runtimeID)
)

const traceTree = createStore<{
  all: TraceTree[]
  top: TraceTree[]
}>({
  all: [],
  top: []
}).on(runtimeID, ({all, top}, runtimeID) => ({
  all: all.filter(record => record.runtimeID === runtimeID),
  top: top.filter(record => record.runtimeID === runtimeID)
}))

const cleanupPosition = createStore({
  top: -1,
  all: -1,
  timer: setInterval(traceTreeCleanup, TIMELINE_MAX)
}).on(runRuntime, ({timer}) => {
  clearInterval(timer)
  return {
    top: -1,
    all: -1,
    timer: setInterval(traceTreeCleanup, TIMELINE_MAX)
  }
})

traceTree.on(
  sample(cleanupPosition, traceTreeCleanup),
  ({top, all}, cleanup) => ({
    top: top.slice(cleanup.top),
    all: all.slice(cleanup.all)
  })
)

cleanupPosition.on(
  sample(traceTree, traceTreeCleanup),
  ({timer}, {top, all}) => ({
    timer,
    top: top.length,
    all: all.length
  })
)

export const topTraces = traceTree.map(({top}) => top)

onRuntimeEvent({
  clock: unitUpdate,
  target: unitUpdates,
  filter: (record, runtimeID) => record.runtimeID === runtimeID,
  fn: (list, items) => [...list, ...items].slice(-MAX_IN_FLIGHT_ITEMS)
})

onRuntimeEvent({
  clock: unitUpdate,
  target: traceTree,
  filter: (record, runtimeID) => record.runtimeID === runtimeID,
  fn({all, top}, items) {
    const currentStacks = all.map(({stack}) => stack)
    all = [...all]
    top = [...top]
    const newRoots = []
    const changedChilds = new Set<number>()
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      const currentStep = item.stack[0]
      const tree: TraceTree = {
        runtimeID: item.runtimeID,
        updateID: item.updateID,
        node: currentStep.node,
        name: printMeta(currentStep.node.meta),
        value: currentStep.value,
        stack: currentStep.stack,
        parent: null,
        child: [],
        used: false,
        nodeType: 'unit'
      }
      if (currentStep.node.meta.op === 'watch') tree.nodeType = 'watch'
      else if (currentStep.node.meta.unit === 'store') tree.nodeType = 'store'
      else if (currentStep.node.meta.unit === 'event') tree.nodeType = 'event'
      else if (currentStep.node.meta.unit === 'effect') tree.nodeType = 'effect'

      // if (tree.name === 'cycleLog [event]') {
      //   console.log(item, items)
      // }
      if (currentStep.value === undefined) {
        tree.value = 'void'
        console.log(item)
      }
      all.push(tree)
      currentStacks.push(tree.stack)
      if (item.stack.length === 1) {
        top.push(tree)
        newRoots.push(tree)
      } else {
        let id = -1
        for (let j = 1; j < item.stack.length && id === -1; j++) {
          id = currentStacks.indexOf(item.stack[j].stack)
        }
        if (id === -1) {
          top.push(tree)
          newRoots.push(tree)
        } else {
          const parent = all[id]
          tree.parent = parent
          parent.child.push(tree)
          changedChilds.add(id)
        }
      }
    }
    for (const id of changedChilds) {
      all[id].child = [...all[id].child]
    }
    for (let i = 0; i < newRoots.length; i++) {
      detectUsed(newRoots[i])
    }
    return {
      all,
      top
    }
  }
})

function detectUsed(tree: TraceTree) {
  let used = false
  if (tree.node.meta.op === 'watch' || tree.node.meta.unit === 'effect') {
    tree.used = true
    used = true
  }
  for (let i = 0; i < tree.child.length; i++) {
    if (detectUsed(tree.child[i])) {
      tree.used = true
      used = true
    }
  }
  return used
}

export const updateStackReceiver = (
  node: Step,
  implicitStack: Stack[],
  prepend: boolean
) => {
  const runner = step.compute({
    //@ts-ignore
    fn(upd, scope, stack: Stack) {
      const {runtimeID} = stack.node.meta
      launch(
        unitUpdate,
        {
          runtimeID,
          updateID: ++nextUpdateID,
          stack: unwind(stack, implicitStack)
        },
        //@ts-ignore
        true
      )
      return upd
    }
  })
  if (prepend) {
    node.seq.unshift(runner)
  } else {
    node.seq.push(runner)
  }
}
let nextUpdateID = 0
